import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import {
  Container,
  Form,
  Card,
  Button,
  InputGroup,
  Row,
  Col,
  Badge,
  ListGroup,
  Image,
  Tab,
  Nav,
} from 'react-bootstrap'
import { Search, X, Plus, Dash, Check, XCircle } from 'react-bootstrap-icons'
import Swal from 'sweetalert2'
import axios from 'axios'
import { motion } from 'framer-motion'

export default function ManageInvites() {
  const { id } = useParams() // Get group ID from URL params
  const [searchTerm, setSearchTerm] = useState('')
  const [inviteMessage, setInviteMessage] = useState('')
  const [selectedMembers, setSelectedMembers] = useState([])
  const [members, setMembers] = useState([])
  const [joinRequests, setJoinRequests] = useState([])

  useEffect(() => {
    fetchMembers()
    fetchJoinRequests()
  }, [])

  const fetchMembers = async () => {
    try {
      const token = localStorage.getItem('jwttoken')
      const response = await axios.get(
        'https://kaasbackend.vetrinahealthcare.com/socialapp/users/',
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )

      setMembers(
        response.data.map((user) => ({
          id: user.user_id,
          name: `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim(),
          email: user.email,
          profilePic:
            user.profile_pic ||
            'https://cdn-icons-png.flaticon.com/512/149/149071.png',
        }))
      )
    } catch (error) {
      console.error('Error fetching members:', error)
    }
  }

  const fetchJoinRequests = async () => {
    try {
      const token = localStorage.getItem('jwttoken')
      const response = await axios.get(
        `https://kaasbackend.vetrinahealthcare.com/socialapp/group/${id}/join-requests/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )

      setJoinRequests(response.data)
    } catch (error) {
      console.error('Error fetching join requests:', error)
    }
  }

  const handleApproveReject = async (requestId, action) => {
    try {
      const token = localStorage.getItem('jwttoken')
      await axios.patch(
        `https://kaasbackend.vetrinahealthcare.com/socialapp/join-requests/${requestId}/`,
        { action },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      // Show success message
      Swal.fire('Success', `Request ${action}ed successfully`, 'success')

      // Update UI state to reflect the change
      setJoinRequests((prevRequests) =>
        prevRequests.map((req) =>
          req.id === requestId
            ? { ...req, status: action === 'approve' ? 'approved' : 'rejected' }
            : req
        )
      )
    } catch (error) {
      Swal.fire('Error', `Failed to ${action} request`, 'error')
    }
  }

  const handleAddMemberToGroup = async (member) => {
    try {
      const token = localStorage.getItem('jwttoken')

      const response = await axios.post(
        `https://kaasbackend.vetrinahealthcare.com/socialapp/groups/${id}/add-member/`, // Use dynamic group ID
        { users: [member.id] }, // Ensure "users" field is an array
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )

      if (response.status === 201) {
        Swal.fire(
          'Success',
          `${member.name} has been added to the group!`,
          'success'
        )
      } else {
        Swal.fire('Error', 'Could not add member to the group.', 'error')
      }
    } catch (error) {
      console.error('Error adding member to group:', error)
      Swal.fire('Error', 'Failed to add member. Please try again.', 'error')
    }
  }

  return (
    <Container className='py-4'>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Card
          style={{
            borderRadius: '15px',
            padding: '20px',
            border: '2px solid #007bff',
          }}
        >
          <Tab.Container defaultActiveKey='sendInvites'>
            <Nav variant='tabs'>
              <Nav.Item>
                <Nav.Link eventKey='sendInvites'>Send Invites</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey='requests'>Requests</Nav.Link>
              </Nav.Item>
            </Nav>

            <Tab.Content className='mt-3'>
              {/* Send Invites Tab */}
              <Tab.Pane eventKey='sendInvites'>
                <Row>
                  <Col lg={5} style={{ borderRight: '2px solid #ddd' }}>
                    <h5 style={{ color: '#8a2be2', fontWeight: 'bold' }}>
                      Search Members
                    </h5>
                    <InputGroup className='mb-3'>
                      <InputGroup.Text style={{ background: '#f8f9fa' }}>
                        <Search />
                      </InputGroup.Text>
                      <Form.Control
                        placeholder='Search...'
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </InputGroup>

                    <div
                      style={{
                        maxHeight: '300px',
                        overflowY: 'auto',
                        border: '1px solid #ddd',
                        borderRadius: '10px',
                        padding: '10px',
                        background: '#f9f9f9',
                      }}
                    >
                      <ListGroup variant='flush'>
                        {members
                          .filter((member) =>
                            member.name
                              .toLowerCase()
                              .includes(searchTerm.toLowerCase())
                          )
                          .map((member) => (
                            <ListGroup.Item
                              key={member.id}
                              className='d-flex align-items-center justify-content-between'
                            >
                              <Image
                                src={member.profilePic}
                                roundedCircle
                                style={{ width: '35px', height: '35px' }}
                                alt='User'
                              />
                              <span>{member.name}</span>
                              <Button
                                variant='outline-primary'
                                onClick={() => handleAddMemberToGroup(member)}
                              >
                                <Plus size={18} />
                              </Button>
                            </ListGroup.Item>
                          ))}
                      </ListGroup>
                    </div>
                  </Col>
                </Row>
              </Tab.Pane>

              {/* Requests Tab */}
              <Tab.Pane eventKey='requests'>
                <h5 style={{ color: '#8a2be2', fontWeight: 'bold' }}>
                  Join Requests
                </h5>
                {joinRequests.map((request) => (
                  <ListGroup.Item
                    key={request.id}
                    className='d-flex justify-content-between align-items-center'
                  >
                    {request.requested_by.name}
                    <div>
                      {request.status === 'pending' ? (
                        <>
                          <Button
                            variant='success'
                            className='me-2'
                            onClick={() =>
                              handleApproveReject(request.id, 'approve')
                            }
                          >
                            <Check />
                          </Button>
                          <Button
                            variant='danger'
                            onClick={() =>
                              handleApproveReject(request.id, 'reject')
                            }
                          >
                            <XCircle />
                          </Button>
                        </>
                      ) : (
                        <Badge
                          bg={
                            request.status === 'approved' ? 'success' : 'danger'
                          }
                        >
                          {request.status.toUpperCase()}
                        </Badge>
                      )}
                    </div>
                  </ListGroup.Item>
                ))}
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Card>
      </motion.div>
    </Container>
  )
}
